import { PopupCancelledError, PopupTimeoutError } from "@auth0/auth0-spa-js";
import * as Core from "@zeller-public/payments-sdk-core";
import { safe } from "pb.safe";
import * as v from "valibot";

import { onRequestQuery } from "../on-request-query";
import { logger } from "../shared/logger";
import { suspend } from "../shared/suspend";
import { tags, tags_fromRequest } from "../tags";
import * as transport from "../transport";
import {
	auth0Config,
	getAuth0Client,
	readTokenCache,
	TOKEN_CACHE_KEY,
	TokenResponseSchema,
	USER_CACHE_KEY,
} from "./shared/auth0";

// ============================================================================

export async function login(
	request: Omit<Core.Protocol.Request_Query, "$type">,
): Promise<void> {
	if (window.self === window.top) {
		await loginWithRedirect(request);
		return suspend();
	}

	return loginWithPopup(request);
}

async function loginWithPopup(
	request: Omit<Extract<Core.Protocol.Request, { $type: "Query" }>, "$type">,
) {
	window.sessionStorage.setItem(
		"zsdk.request",
		JSON.stringify({
			$type: "Query",
			...request,
		}),
	);

	let popup: Window | null = null;

	try {
		const width = 800;
		const height = 600;

		const left = screen.width / 2 - width / 2;
		const top = screen.height / 2 - height / 2;

		const options = `width=${width},height=${height},top=${top},left=${left}`;
		// Open the popup synchronously off the click's user activation, before
		// any await — Chrome silently drops window.open() once that activation
		// expires, which it can during the network calls inside getAuth0Client().
		popup = window.open("", "Login to Zeller Payments Sdk", options);

		const client = await getAuth0Client();

		try {
			await client.loginWithPopup(
				// Force the Auth0 login page even if a stale SSO session survives
				// (e.g. logout could not clear the third-party cookie in a WebView),
				// otherwise re-login silently reuses the old session.
				{ authorizationParams: { prompt: "login" } },
				{ closePopup: false, popup, timeoutInSeconds: 300 },
			);
		} catch (error) {
			if (error instanceof PopupCancelledError) {
				logger.error("auth: login with popup cancelled", {
					message: error.message,
					name: error.name,
					stack: error.stack,
				});
			}
			if (error instanceof PopupTimeoutError) {
				logger.error("auth: login with popup timeout", {
					message: error.message,
					name: error.name,
					stack: error.stack,
				});
			}

			return transport.Resolve.Error("Cancelled", {
				message: "The login window was closed before sign-in completed.",
			});
		} finally {
			popup?.close();
		}

		window.location.replace(`${auth0Config.redirectUri}/login-callback`);
	} catch (error) {
		popup?.close();

		if (error instanceof Error) {
			logger.error("auth: login with popup", {
				message: error.message,
				name: error.name,
				stack: error.stack,
			});
		} else {
			logger.error("auth: login with popup unknown error");
		}
	}
}

async function loginWithRedirect(
	request: Omit<Core.Protocol.Request_Query, "$type">,
): Promise<void> {
	window.sessionStorage.setItem(
		"zsdk.request",
		JSON.stringify({
			$type: "Query",
			...request,
		}),
	);

	logger.debug("auth: login redirecting");

	// Prevent back navigation from Auth0 which will result in white-screen.
	window.location.replace(authorizeUrl());
}

export function authorizeUrl(): string {
	const params = new URLSearchParams({
		client_id: auth0Config.clientId,
		redirect_uri: `${auth0Config.redirectUri}/login-callback`,
		scope: auth0Config.scope,
		audience: auth0Config.audience,
		response_type: "code",
		// Force the login page rather than silently reusing a stale SSO session
		// (logout may not clear the Auth0 cookie in an embedded WebView).
		prompt: "login",
		auth0Client: "eyJuYW1lIjoiYXV0aDAuanMiLCJ2ZXJzaW9uIjoiOS4yOC4wIn0=", // '{"name":"auth0.js","version":"9.28.0"}'
	}).toString();

	return `https://${auth0Config.domain}/authorize?${params}`;
}

// ============================================================================

export async function loginCallback(): Promise<Core.Protocol.Request_Query | void> {
	logger.debug("auth: login callback");

	if (window.self === window.top) {
		return loginRedirectCallback();
	}

	return loginPopupCallback();
}

export async function loginPopupCallback(): Promise<Core.Protocol.Request_Query | void> {
	logger.info("auth: login popup callback");

	const requestJson =
		window.sessionStorage.getItem("zsdk.request") ?? undefined;
	window.sessionStorage.removeItem("zsdk.request");
	if (!requestJson) {
		logger.error("auth: login popup callback missing request");
		return transport.Resolve.Error("Operation Failure", {
			message: "Unable to retrieve request from storage.",
		});
	}

	const request = safe(() =>
		v.parse(Core.Protocol.Request, JSON.parse(requestJson)),
	);
	if (request instanceof Error || request.$type !== "Query") {
		logger.error("auth: login popup callback request json parse error");
		return transport.Resolve.Error("Operation Failure", {
			message: "Invalid/corrupted request from storage.",
		});
	}

	tags(tags_fromRequest(request));

	const tokens = safe(() => TokenResponseSchema.parse(readTokenCache()));

	if (tokens instanceof Error) {
		logger.error("auth: login popup callback token response parse error");
		return transport.Resolve.Error("Operation Failure", {
			message: "Token cache empty or invalid after popup login.",
		});
	}

	// clear auth0 token state as no longer required, we'll use our own
	// configuration state for subsequent requests
	window.localStorage.removeItem(TOKEN_CACHE_KEY);
	window.localStorage.removeItem(USER_CACHE_KEY);

	onRequestQuery({
		...request,
		headers: {
			...request.headers,
			authAccessToken: tokens.access_token,
			authIdToken: tokens.id_token,
			authRefreshToken: tokens.refresh_token,
		},
	});
}

export async function loginRedirectCallback(): Promise<Core.Protocol.Request_Query | void> {
	logger.info("auth: login redirect callback");

	// Get params and cleanup quickly.
	const params = new URLSearchParams(window.location.search.substring(1));
	window.history.replaceState({}, "", "/");

	// Restore request, fail as early as possible.
	const requestJson =
		window.sessionStorage.getItem("zsdk.request") ?? undefined;
	window.sessionStorage.removeItem("zsdk.request");
	if (!requestJson) {
		logger.error("auth: login redirect callback request json parse error");
		return void transport.Resolve.Error("Operation Failure", {
			message: "Unable to retrieve request from storage.",
		});
	}

	const request = safe(() =>
		v.parse(Core.Protocol.Request, JSON.parse(requestJson)),
	);
	if (request instanceof Error || request.$type !== "Query") {
		logger.error("auth: login redirect callback request json parse error");
		return void transport.Resolve.Error("Operation Failure", {
			message: "Invalid/corrupted request from storage.",
		});
	}

	tags(tags_fromRequest(request));

	const code = params.get("code") ?? undefined;
	if (!code) {
		logger.error("auth: login redirect callback missing code param");
		return transport.Resolve.Error("Operation Failure", {
			message: "Missing auth callback code.",
		});
	}

	const tokenResponse = await safe(async () => {
		const json = await fetch(`https://${auth0Config.domain}/oauth/token`, {
			method: "POST",
			body: new URLSearchParams({
				client_id: auth0Config.clientId,
				redirect_uri: auth0Config.redirectUri,
				grant_type: "authorization_code",
				code,
			}),
		}).then((response) => response.json());

		const tokens = TokenResponseSchema.safeParse(json);
		if (!tokens.success) {
			// Surface Auth0's error (never the tokens) so the failure is diagnosable.
			const { error, error_description } = json as {
				error?: string;
				error_description?: string;
			};
			throw new Error(error_description ?? error ?? "Invalid token response.");
		}

		return tokens.data;
	});
	if (tokenResponse instanceof Error) {
		logger.error("auth: login redirect callback token exchange", {
			message: tokenResponse.message,
			name: tokenResponse.name,
			stack: tokenResponse.stack,
		});
		return transport.Resolve.Error("Operation Failure", {
			message:
				"Failed to exchange auth callback url parameters to token with Auth0.",
		});
	}

	const config = {
		authAccessToken: tokenResponse.access_token,
		authRefreshToken: tokenResponse.refresh_token,
		authIdToken: tokenResponse.id_token,
	};

	transport.Config(config);

	onRequestQuery({
		...request,
		headers: {
			...request.headers,
			...config,
		},
	});
}

// ============================================================================

export async function logout(request: {
	headers: { authIdToken?: string };
}): Promise<void> {
	logger.debug("auth: logout");

	if (window.self === window.top) {
		await logoutWithRedirect(request);
		return;
	}

	return logoutInFrame();
}

async function logoutInFrame(): Promise<void> {
	logger.info("auth: logout in frame");

	window.localStorage.clear();

	try {
		const client = await getAuth0Client();

		// Navigate this iframe to Auth0's logout endpoint, then suspend. Auth0
		// clears the SSO session and redirects back to `/logout-callback`, which
		// `app.tsx` routes to `logoutCallback()` to resolve. Suspending (rather
		// than resolving now) stops the host tearing the iframe down before
		// `/v2/logout` completes — otherwise the SSO session is never cleared and
		// the next login silently reuses it.
		await client.logout({
			logoutParams: {
				returnTo: `${auth0Config.redirectUri}/logout-callback`,
			},
		});
	} catch (error) {
		if (error instanceof Error) {
			logger.error("auth: error on logout in frame", {
				message: error.message,
				name: error.name,
				stack: error.stack,
			});
		} else {
			logger.error("auth: logout in frame unknown error");
		}

		return logoutCallback();
	}

	return suspend();
}

export async function logoutWithRedirect(request: {
	headers: { authIdToken?: string };
}): Promise<void> {
	logger.info("auth: logout redirect callback");

	if (request.headers.authIdToken) {
		/* v8 ignore next 4 -- navigates the window and suspends forever; untestable in the browser runner per the repo no-navigation rule (logoutUrl is covered separately) */
		logger.debug("auth: logout with redirect");
		window.location.href = logoutUrl(request.headers.authIdToken);

		return suspend();
	}

	return logoutCallback();
}

export function logoutUrl(idToken: string): string {
	const params = new URLSearchParams({
		id_token_hint: idToken,
		post_logout_redirect_uri: `${window.location.origin}/logout-callback`,
	}).toString();

	return `https://${auth0Config.domain}/oidc/logout?${params}`;
}

// ============================================================================

export async function logoutCallback(): Promise<void> {
	window.history.replaceState({}, "", "/");

	return void transport.Resolve.Error("Setup Required", {
		message: "Logged out.",
		reset: true,
	});
}
